/**
 * ShareLinkResult - Display created share link with copy functionality
 */

import React from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useClipboard,
} from '@chakra-ui/react';
import { Copy, Check, Plus } from 'lucide-react';
import { CreateShortLinkResponse } from '../../types/share.types';
import { chartShareService } from '../../services/ChartShareService';

interface ShareLinkResultProps {
  result: CreateShortLinkResponse;
  onCreateAnother: () => void;
}

export function ShareLinkResult({ result, onCreateAnother }: ShareLinkResultProps) {
  const { onCopy, hasCopied } = useClipboard(result.full_url);

  return (
    <VStack spacing={4} align="stretch">
      {/* Success Message */}
      <Alert status="success" borderRadius="md">
        <AlertIcon />
        <Text fontWeight="medium">Short link created successfully!</Text>
      </Alert>

      {/* Link Display */}
      <Box>
        <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">
          Your share link:
        </Text>
        <HStack>
          <Input
            value={result.full_url}
            isReadOnly
            size="md"
            fontFamily="mono"
            fontSize="sm"
          />
          <Button
            onClick={onCopy}
            colorScheme={hasCopied ? 'green' : 'blue'}
            leftIcon={hasCopied ? <Check size={16} /> : <Copy size={16} />}
            minW="100px"
          >
            {hasCopied ? 'Copied!' : 'Copy'}
          </Button>
        </HStack>
      </Box>

      <Divider />

      {/* Metadata */}
      <SimpleGrid columns={2} spacing={4}>
        <Stat size="sm">
          <StatLabel fontSize="xs" color="gray.600">Expires</StatLabel>
          <StatNumber fontSize="md">
            {chartShareService.formatExpiryMessage(result.expires_at)}
          </StatNumber>
        </Stat>
        <Stat size="sm">
          <StatLabel fontSize="xs" color="gray.600">Created</StatLabel>
          <StatNumber fontSize="md">
            {new Date(result.created_at).toLocaleDateString()}
          </StatNumber>
        </Stat>
      </SimpleGrid>

      <Divider />

      {/* Actions */}
      <Button
        variant="outline"
        onClick={onCreateAnother}
        leftIcon={<Plus size={16} />}
        size="sm"
      >
        Create Another Link
      </Button>

      {/* Info */}
      <Alert status="info" variant="subtle" fontSize="sm">
        <AlertIcon />
        <Text fontSize="xs">
          Share this link with anyone. They will see only the sections and fields you selected.
        </Text>
      </Alert>
    </VStack>
  );
}
